import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Collection from 'assets/components/data/Collection';
import ThemeCollectionTable from 'assets/components/data/themes/Theme.Collection.Table';
import DataMap from 'assets/components/dataMap/DataMap';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import { getLocales } from 'assets/locales/Locale';
import { parseRangeValue } from 'assets/utils/parsersAndValidation/Parsers';
import ConditionTypes from 'models/enums/ConditionTypes';
import EffectTypes from 'models/enums/EffectTypes';
import { Fragment, useState } from 'react';
import { useClaimTypeProvider } from '../../../ClaimType.Hooks';
import ClaimTypeAutomationRuleEditForm from './ClaimType.Edit.AutomationRuleEditForm';
import claimTypeAutomationRuleDeleteApi from 'models/claims/claimTypeAutomationRule/delete/Api.ClaimTypeAutomationRule.Delete';
import { HttpStatus } from 'config/Api.Config';
import { addLog } from 'assets/components/feedback/Feedback';

export default function ClaimTypeEditAutomationRules() {
  const { lang } = getLocales();
  const { form } = useModalForm<Api.Core.ClaimType.Save.IRequest>();
  const [currentCondition, setCurrentCondition] = useState<Partial<Model.IClaimTypeAutomationRule>>(null);
  const { claimStatuses, claimJobStatuses } = useClaimTypeProvider();

  const updateCondition = (updatedCondition: any, index: number) => {
    const updatedConditions = form.data?.claimTypeAutomationRules?.map((item, idx) =>
      idx === index ? updatedCondition : item
    );
    form.update({ claimTypeAutomationRules: updatedConditions });
  };

  const deleteCondition = async (index: number, ruleId?: number) => {
    const updatedConditions = form.data?.claimTypeAutomationRules?.filter((_, idx) => idx !== index);
    form.update({ claimTypeAutomationRules: updatedConditions });

    if (ruleId) {
      const res = await claimTypeAutomationRuleDeleteApi(ruleId);
      if (res.status !== HttpStatus.ok) {
        addLog({ error: res.message });
      }
    }
  };

  return (
    <Collection class={ThemeCollectionTable}>
      <Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <Button
          class={ThemeButtonCircle}
          render={lang.addNew}
          active
          disabled={form.data.claimTypeAutomationRules?.length >= 1}
          data={{
            id: -1 * (form.data.claimTypeAutomationRules?.length || 0),
            conditions: [{ type: ConditionTypes.Equal, propertyPath: 'Claim.TotalAmountCAD', value: '' }],
            effects: [
              { type: EffectTypes.Assign, propertyPath: 'Claim.StatusCode', value: '' },
              { type: EffectTypes.Assign, propertyPath: 'Claim.ClaimJobs[].StatusCode', value: '' },
            ],
          }}
          onClick={setCurrentCondition}
        />
      </Box>

      {currentCondition && (
        <ClaimTypeAutomationRuleEditForm
          data={currentCondition}
          onCancel={() => setCurrentCondition(null)}
          onSave={(newCondition) => {
            const filteredConditions = form.data?.claimTypeAutomationRules?.filter((it) => it?.id !== newCondition?.id);
            form.update({ claimTypeAutomationRules: [...(filteredConditions || []), newCondition] });
            setCurrentCondition(null);
          }}
        />
      )}

      <DataMap
        data={form.data?.claimTypeAutomationRules}
        render={({ data: conditionItem, index }) => {
          const isRange = conditionItem.conditions?.[0]?.type === ConditionTypes.Range;
          const rangeValue = isRange ? parseRangeValue(conditionItem.conditions?.[0]?.value) : null;

          return (
            <Box
              key={`${conditionItem?.id}_${index}`}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}
            >
              <Fragment key={conditionItem.id}>
                If Claim TotalAmountCAD is:
                <SelectInput
                  data={Object.values(ConditionTypes).map((it) => ({ id: it, title: it }))}
                  value={conditionItem.conditions?.[0]?.type}
                  onChange={({ type }) => {
                    const baseValue = conditionItem.conditions?.[0]?.value;
                    if (type === ConditionTypes.Range) {
                      updateCondition(
                        {
                          ...conditionItem,
                          conditions: [
                            {
                              type: ConditionTypes.Range,
                              propertyPath: 'Claim.TotalAmountCAD',
                              value: JSON.stringify({
                                min: parseRangeValue(baseValue)?.min,
                                max: parseRangeValue(baseValue)?.max,
                              }),
                            },
                          ],
                        },
                        index
                      );
                    } else {
                      let singleValue = '';
                      try {
                        const parsed = JSON.parse(baseValue);
                        singleValue = typeof parsed === 'object' && parsed.min !== undefined ? parsed.min : baseValue;
                      } catch {
                        singleValue = baseValue;
                      }
                      updateCondition(
                        {
                          ...conditionItem,
                          conditions: [
                            {
                              type,
                              propertyPath: 'Claim.TotalAmountCAD',
                              value: singleValue?.toString(),
                            },
                          ],
                        },
                        index
                      );
                    }
                  }}
                  name="type"
                />
                {!isRange ? (
                  <>
                    to:
                    <NumberInput
                      name="value"
                      step={0.01}
                      value={Number(conditionItem.conditions?.[0]?.value?.toString())}
                      onChange={({ value }) => {
                        updateCondition(
                          {
                            ...conditionItem,
                            conditions: [
                              {
                                ...conditionItem.conditions[0],
                                value: value?.toString(),
                              },
                            ],
                          },
                          index
                        );
                      }}
                    />
                  </>
                ) : (
                  <>
                    between
                    <NumberInput
                      name="minValue"
                      step={0.01}
                      value={rangeValue?.min ?? 0}
                      onChange={({ minValue }) => {
                        updateCondition(
                          {
                            ...conditionItem,
                            conditions: [
                              {
                                ...conditionItem.conditions[0],
                                value: JSON.stringify({
                                  min: minValue,
                                  max: rangeValue?.max ?? 0,
                                }),
                              },
                            ],
                          },
                          index
                        );
                      }}
                    />
                    and
                    <NumberInput
                      name="maxValue"
                      step={0.01}
                      value={rangeValue?.max ?? 0}
                      onChange={({ maxValue }) => {
                        updateCondition(
                          {
                            ...conditionItem,
                            conditions: [
                              {
                                ...conditionItem.conditions[0],
                                value: JSON.stringify({
                                  min: rangeValue?.min ?? 0,
                                  max: maxValue,
                                }),
                              },
                            ],
                          },
                          index
                        );
                      }}
                    />
                  </>
                )}
                set ClaimStatus to:
                <SelectInput
                  name="claimStatus"
                  data={claimStatuses}
                  value={conditionItem.effects?.find((e) => e.propertyPath === 'Claim.StatusCode')?.value || ''}
                  onChange={({ claimStatus }) => {
                    const newEffects = [...(conditionItem.effects || [])];
                    const idx = newEffects.findIndex((e) => e.propertyPath === 'Claim.StatusCode');
                    if (idx >= 0) {
                      newEffects[idx] = { ...newEffects[idx], value: claimStatus };
                    } else {
                      newEffects.push({
                        type: EffectTypes.Assign,
                        propertyPath: 'Claim.StatusCode',
                        value: claimStatus,
                      });
                    }
                    updateCondition({ ...conditionItem, effects: newEffects }, index);
                  }}
                />
                and set ClaimJobStatus to:
                <SelectInput
                  name="claimJobStatus"
                  data={claimJobStatuses}
                  value={
                    conditionItem.effects?.find((e) => e.propertyPath === 'Claim.ClaimJobs[].StatusCode')?.value || ''
                  }
                  onChange={({ claimJobStatus }) => {
                    const newEffects = [...(conditionItem.effects || [])];
                    const idx = newEffects.findIndex((e) => e.propertyPath === 'Claim.ClaimJobs[].StatusCode');
                    if (idx >= 0) {
                      newEffects[idx] = { ...newEffects[idx], value: claimJobStatus };
                    } else {
                      newEffects.push({
                        type: EffectTypes.Assign,
                        propertyPath: 'Claim.ClaimJobs[].StatusCode',
                        value: claimJobStatus,
                      });
                    }
                    updateCondition({ ...conditionItem, effects: newEffects }, index);
                  }}
                />
                <Button
                  class={ThemeButtonCircle}
                  media={<Icon class="fas-trash" />}
                  data={{ index, ruleId: conditionItem?.id }}
                  onClick={({ index, ruleId }) => deleteCondition(index, ruleId)}
                />
              </Fragment>
            </Box>
          );
        }}
      />
    </Collection>
  );
}
